import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Settings, Upload, Image, Type, Palette, Save, RotateCcw, Key, Eye, EyeOff } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import MfaSettingsCard from '@/components/MfaSettingsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettingsPage = ({ user, onLogout }) => {
  const { settings, refreshSettings } = useSettings();
  const [formData, setFormData] = useState({
    app_name: '',
    app_subtitle: '',
    primary_color: '#f97316'
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData({
        app_name: settings.app_name || 'EdTech',
        app_subtitle: settings.app_subtitle || 'Sistema de Gestión Escolar',
        primary_color: settings.primary_color || '#f97316'
      });
    }
  }, [settings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración guardada');
      refreshSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes (PNG, JPG, SVG, WEBP)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo no debe superar 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      await axios.post(`${API}/admin/settings/upload-logo`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Logo actualizado');
      refreshSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('¿Estás seguro de eliminar el logo personalizado?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings`, { logo_url: '' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Logo eliminado');
      refreshSettings();
    } catch (error) {
      toast.error('Error al eliminar logo');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contraseña actualizada correctamente');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 md:p-8 lg:p-12">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Configuración
          </h1>
          <p className="text-base text-slate-600">Personaliza la apariencia de la aplicación</p>
        </div>

        <div className="grid gap-6 max-w-2xl">
          {/* Logo Section */}
          <Card className="p-6 bg-white border border-slate-100 rounded-xl shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Image className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Logo</h2>
                <p className="text-sm text-slate-600">Sube un logo personalizado para la aplicación</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-slate-400 text-sm text-center px-2">Sin logo</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="bg-purple-500 hover:bg-purple-600"
                  data-testid="upload-logo-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingLogo ? 'Subiendo...' : 'Subir Logo'}
                </Button>
                {settings.logo_url && (
                  <Button
                    onClick={handleRemoveLogo}
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Usar Predeterminado
                  </Button>
                )}
                <p className="text-xs text-slate-500">PNG, JPG, SVG o WEBP. Máx 2MB.</p>
              </div>
            </div>
          </Card>

          {/* Texts Section */}
          <Card className="p-6 bg-white border border-slate-100 rounded-xl shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Type className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Textos</h2>
                <p className="text-sm text-slate-600">Personaliza los textos de la aplicación</p>
              </div>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="app_name">Nombre de la Aplicación</Label>
                <Input
                  id="app_name"
                  value={formData.app_name}
                  onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                  placeholder="EdTech"
                  data-testid="app-name-input"
                />
                <p className="text-xs text-slate-500">Se muestra en la barra lateral y encabezado</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="app_subtitle">Subtítulo / Descripción</Label>
                <Input
                  id="app_subtitle"
                  value={formData.app_subtitle}
                  onChange={(e) => setFormData({ ...formData, app_subtitle: e.target.value })}
                  placeholder="Sistema de Gestión Escolar"
                  data-testid="app-subtitle-input"
                />
                <p className="text-xs text-slate-500">Descripción corta de la aplicación</p>
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="save-settings-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </form>
          </Card>

          {/* Preview Section */}
          <Card className="p-6 bg-white border border-slate-100 rounded-xl shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Vista Previa</h2>
                <p className="text-sm text-slate-600">Así se verá la barra lateral</p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                {settings.logo_url ? (
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                    <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">{formData.app_name?.charAt(0) || 'E'}</span>
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-slate-900">{formData.app_name || 'EdTech'}</h3>
                  <p className="text-xs text-slate-600">{formData.app_subtitle || 'Sistema de Gestión Escolar'}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Change Password Section */}
          <Card className="p-6 bg-white border border-slate-100 rounded-xl shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Key className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Seguridad</h2>
                <p className="text-sm text-slate-600">Cambia la contraseña de tu cuenta de administrador</p>
              </div>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Contraseña Actual</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Ingresa tu contraseña actual"
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_password">Nueva Contraseña</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Ingresa tu nueva contraseña"
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Mínimo 6 caracteres</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmar Nueva Contraseña</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Confirma tu nueva contraseña"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={changingPassword || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
                className="bg-red-500 hover:bg-red-600"
                data-testid="change-password-button"
              >
                <Key className="w-4 h-4 mr-2" />
                {changingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
              </Button>
            </form>
          </Card>

          {/* MFA (verificación en dos pasos) */}
          <MfaSettingsCard />
        </div>
      </div>
    </Layout>
  );
};

export default AdminSettingsPage;
